import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  useCountries,
  useStatesByCountry,
  extractData,
} from "../../../../hooks/useLocations";
import { usePostalCodeValidation } from "../../../../hooks/usePostalCodeValidation";
import { Country, State } from "../../../../services/locationService";
import { SmartLocationFieldsProps } from "./types";

const isUSCountry = (country: Country | null): boolean => {
  if (!country) return false;
  const iso2 = (country.iso2 || "").toUpperCase();
  const iso3 = (country.iso3 || "").toUpperCase();
  const name = (country.name || "").toLowerCase();
  return (
    iso2 === "US" ||
    iso3 === "USA" ||
    name === "united states" ||
    name === "united states of america"
  );
};

// Autocomplete dropdown for country/state selection
interface AutocompleteFieldProps<T> {
  label: string;
  placeholder?: string;
  value: T | null;
  options: T[];
  getLabel: (item: T) => string;
  onChange: (item: T | null) => void;
  onSearch: (q: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

function AutocompleteField<T extends { id: number | null }>({
  label,
  placeholder,
  value,
  options,
  getLabel,
  onChange,
  onSearch,
  isLoading,
  disabled,
  required,
  error,
  helperText,
}: AutocompleteFieldProps<T>) {
  const [inputValue, setInputValue] = useState(value ? getLabel(value) : "");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value ? getLabel(value) : "");
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        // Revert input to selected value if user didn't pick
        setInputValue(value ? getLabel(value) : "");
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, value, getLabel]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setInputValue(q);
    onSearch(q);
    setOpen(true);
    if (q === "") onChange(null);
  };

  const handleSelect = (item: T) => {
    onChange(item);
    setInputValue(getLabel(item));
    setOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
        {label}{required && <span style={{ color: "var(--err)" }}> *</span>}
      </label>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          placeholder={placeholder || `Select ${label.toLowerCase()}`}
          style={{
            width: "100%",
            padding: "7px 32px 7px 10px",
            border: `1px solid ${error ? "var(--err)" : "var(--line)"}`,
            borderRadius: 6,
            fontSize: 13,
            background: disabled ? "var(--bg-tint)" : "var(--bg-elev)",
            color: disabled ? "var(--ink-4)" : "var(--ink)",
            outline: "none",
            boxSizing: "border-box",
            cursor: disabled ? "not-allowed" : "text",
          }}
        />
        {isLoading && (
          <span
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 12,
              color: "var(--ink-4)",
            }}
          >
            …
          </span>
        )}
      </div>
      {helperText && (
        <p style={{ fontSize: 11, color: error ? "var(--err)" : "var(--ink-4)", margin: "3px 0 0" }}>
          {helperText}
        </p>
      )}
      {open && options.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 900,
            background: "var(--bg-elev)",
            border: "1px solid var(--line)",
            borderRadius: 6,
            boxShadow: "var(--shd-2)",
            maxHeight: 200,
            overflowY: "auto",
            marginTop: 2,
          }}
        >
          {options.map((item) => (
            <button
              key={String(item.id)}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(item); }}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "none",
                background: value?.id === item.id ? "rgba(43,85,255,0.08)" : "transparent",
                cursor: "pointer",
                textAlign: "left",
                fontSize: 13,
                color: value?.id === item.id ? "var(--brand-blue)" : "var(--ink)",
                fontWeight: value?.id === item.id ? 600 : 400,
              }}
              onMouseEnter={(e) => {
                if (value?.id !== item.id)
                  (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-tint)";
              }}
              onMouseLeave={(e) => {
                if (value?.id !== item.id)
                  (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              }}
            >
              {getLabel(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const inputStyle = (error?: boolean, disabled?: boolean): React.CSSProperties => ({
  width: "100%",
  padding: "7px 10px",
  border: `1px solid ${error ? "var(--err)" : "var(--line)"}`,
  borderRadius: 6,
  fontSize: 13,
  background: disabled ? "var(--bg-tint)" : "var(--bg-elev)",
  color: disabled ? "var(--ink-4)" : "var(--ink)",
  outline: "none",
  boxSizing: "border-box" as const,
  cursor: disabled ? "not-allowed" : "text",
});

const SmartLocationFields = React.memo<SmartLocationFieldsProps>(
  ({ form, onFormChange, allowedCountryId, onPostalCodeError }) => {
    const {
      isValid: isPostalCodeValid,
      message: postalCodeError,
      isValidating: isValidatingPostal,
      validatePostalCode,
      clearValidation: clearPostalValidation,
    } = usePostalCodeValidation({ debounceMs: 800 });

    const [countrySearch, setCountrySearch] = useState("");
    const [stateSearch, setStateSearch] = useState("");
    const [debouncedCountrySearch, setDebouncedCountrySearch] = useState("");
    const [debouncedStateSearch, setDebouncedStateSearch] = useState("");

    useEffect(() => {
      const t = setTimeout(() => setDebouncedCountrySearch(countrySearch), 300);
      return () => clearTimeout(t);
    }, [countrySearch]);

    useEffect(() => {
      const t = setTimeout(() => setDebouncedStateSearch(stateSearch), 300);
      return () => clearTimeout(t);
    }, [stateSearch]);

    const { data: countriesResponse, isLoading: isLoadingCountries } = useCountries({
      search: debouncedCountrySearch || undefined,
      page: 1,
      per_page: 15,
      select_country: form.country_id || allowedCountryId || undefined,
    });

    const { data: statesResponse, isLoading: isLoadingStates } = useStatesByCountry(
      form.country_id || undefined,
      {
        search: debouncedStateSearch || undefined,
        page: 1,
        per_page: 15,
        select_state: form.state_id || undefined,
      }
    );

    const countries = useMemo(
      () => extractData<Country>(countriesResponse) as Country[],
      [countriesResponse]
    );
    const states = useMemo(
      () => extractData<State>(statesResponse) as State[],
      [statesResponse]
    );

    useEffect(() => {
      if (allowedCountryId && !form.country_id && countries.length > 0) {
        const allowed = countries.find((c) => c.id === allowedCountryId);
        if (allowed) onFormChange("country_id", allowed.id);
      }
    }, [allowedCountryId, form.country_id, countries, onFormChange]);

    const selectedCountry = useMemo(
      () => countries.find((c) => c.id === form.country_id) || null,
      [countries, form.country_id]
    );
    const selectedState = useMemo(
      () => states.find((s) => s.id === form.state_id) || null,
      [states, form.state_id]
    );

    const useStateSelect = isUSCountry(selectedCountry);
    const cityMissing = !form.city_other || form.city_other.trim().length === 0;

    // Convert state_id -> state_other for non-US
    useEffect(() => {
      if (useStateSelect || !form.state_id || form.state_other) return;
      if (!selectedState?.name) return;
      onFormChange("state_other", selectedState.name);
      onFormChange("state_id", null);
    }, [useStateSelect, form.state_id, form.state_other, selectedState, onFormChange]);

    const handleCountryChange = useCallback(
      (country: Country | null) => {
        if (form.country_id !== (country?.id ?? null)) {
          onFormChange("country_id", country?.id ?? null);
          onFormChange("state_id", null);
          onFormChange("state_other", "");
          onFormChange("city_id", null);
          onFormChange("city_other", "");
          setStateSearch("");
        }
      },
      [form.country_id, onFormChange]
    );

    const handleStateChange = useCallback(
      (state: State | null) => {
        if (form.state_id !== (state?.id ?? null)) {
          onFormChange("state_id", state?.id ?? null);
          onFormChange("state_other", "");
        }
      },
      [form.state_id, onFormChange]
    );

    const handleStateOtherChange = useCallback(
      (val: string) => {
        if (form.state_other !== val) {
          onFormChange("state_other", val);
          onFormChange("state_id", null);
        }
      },
      [form.state_other, onFormChange]
    );

    const handleCityOtherChange = useCallback(
      (val: string) => {
        if (form.city_other !== val) {
          onFormChange("city_other", val);
          onFormChange("city_id", null);
        }
      },
      [form.city_other, onFormChange]
    );

    useEffect(() => {
      if (form.zipCode && selectedState && selectedCountry) {
        validatePostalCode(form.zipCode, selectedState, selectedCountry, states, countries);
      } else {
        clearPostalValidation();
      }
    }, [form.zipCode, selectedState, selectedCountry, states, countries, validatePostalCode, clearPostalValidation]);

    useEffect(() => {
      if (onPostalCodeError) {
        onPostalCodeError(isPostalCodeValid ? null : postalCodeError || null);
      }
    }, [isPostalCodeValid, postalCodeError, onPostalCodeError]);

    const fieldLabel = (label: string, required?: boolean) => (
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>
        {label}{required && <span style={{ color: "var(--err)" }}> *</span>}
      </label>
    );

    return (
      <div style={{ width: "100%" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px" }}>
          {/* Country */}
          <AutocompleteField<Country>
            label="Country"
            value={selectedCountry}
            options={countries}
            getLabel={(c) => c.name}
            onChange={handleCountryChange}
            onSearch={setCountrySearch}
            isLoading={isLoadingCountries}
            required
            error={!form.country_id}
            helperText={!form.country_id ? "Required" : ""}
          />

          {/* State */}
          {useStateSelect ? (
            <AutocompleteField<State>
              label="State/Province"
              value={selectedState}
              options={states}
              getLabel={(s) => s.name}
              onChange={handleStateChange}
              onSearch={setStateSearch}
              isLoading={isLoadingStates}
              disabled={!form.country_id}
              placeholder={
                !form.country_id
                  ? "Select country first"
                  : isLoadingStates
                  ? "Loading states..."
                  : "Select state"
              }
            />
          ) : (
            <div>
              {fieldLabel("State/Province")}
              <input
                type="text"
                disabled={!form.country_id}
                value={form.state_other}
                onChange={(e) => handleStateOtherChange(e.target.value)}
                placeholder={!form.country_id ? "Select country first" : "Enter state/province"}
                style={inputStyle(false, !form.country_id)}
              />
            </div>
          )}

          {/* City */}
          <div style={{ gridColumn: "1 / span 1" }}>
            {fieldLabel("City", true)}
            <input
              type="text"
              required
              disabled={!form.country_id}
              value={form.city_other}
              onChange={(e) => handleCityOtherChange(e.target.value)}
              placeholder={!form.country_id ? "Select country first" : "Enter city"}
              style={inputStyle(Boolean(form.country_id) && cityMissing, !form.country_id)}
            />
            {Boolean(form.country_id) && cityMissing && (
              <p style={{ fontSize: 11, color: "var(--err)", margin: "3px 0 0" }}>Required</p>
            )}
          </div>

          {/* Postal code */}
          <div>
            {fieldLabel("Postal Code", true)}
            <div style={{ position: "relative" }}>
              <input
                type="text"
                required
                value={form.zipCode}
                onChange={(e) => onFormChange("zipCode", e.target.value)}
                style={inputStyle(!isPostalCodeValid && !!postalCodeError)}
              />
              {isValidatingPostal && (
                <span
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 11,
                    color: "var(--ink-4)",
                  }}
                >
                  …
                </span>
              )}
            </div>
            {(isValidatingPostal || (!isPostalCodeValid && postalCodeError)) && (
              <p
                style={{
                  fontSize: 11,
                  color: isValidatingPostal ? "var(--ink-4)" : "var(--err)",
                  margin: "3px 0 0",
                }}
              >
                {isValidatingPostal ? "Validating..." : postalCodeError}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
);

SmartLocationFields.displayName = "SmartLocationFields";

export default SmartLocationFields;
